import { apiClient } from "./client";
import { User, ApiResponse } from "../types";

export interface LoginResponse {
  user: User;
  access_token: string;
  refresh_token?: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  message: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;

export class AuthAPI {
  static async login(): Promise<ApiResponse<LoginResponse>> {
    // For OAuth2/Auth0 flow, redirect to backend login endpoint
    // Pass current URL as next_url so that project_id and other query params are preserved after auth
    const nextUrl = encodeURIComponent(window.location.href);
    window.location.href = `${API_BASE_URL}/v1/auth/login?prompt=login&next_url=${nextUrl}`;
    return { success: true };
  }

  static async logout(): Promise<ApiResponse<void>> {
    try {
      window.location.href = `${API_BASE_URL}/v1/auth/logout`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: "Logout failed",
      };
    }
  }

  static async refreshToken(): Promise<ApiResponse<RefreshTokenResponse>> {
    return apiClient.post<RefreshTokenResponse>("/auth/refresh_token");
  }

  static async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      // Use the dedicated /auth/me endpoint to get real user information
      const response = await apiClient.get<any>("/auth/me");
      console.log("🔍 Raw /auth/me response:", response);

      // Backend returns { success: true, data: { id, name, email, iconUrl } }
      // But apiClient.get wraps it again, so we get { success: true, data: { success: true, data: { ... } } }
      if (response.success && response.data) {
        if (response.data.success && response.data.data) {
          // Handle double-nested response
          return {
            success: true,
            data: response.data.data,
          };
        } else if (response.data.id) {
          // Handle direct user data
          return {
            success: true,
            data: response.data,
          };
        }
      }

      return {
        success: false,
        error: "Invalid response format",
      };
    } catch (error) {
      return {
        success: false,
        error: "Not authenticated",
      };
    }
  }

  static async checkAuthStatus(): Promise<boolean> {
    try {
      console.log("🔍 Checking auth status with /auth/me...");
      // Check if user has valid session by trying to access the lightweight auth endpoint
      const response = await apiClient.get<any>("/auth/me");
      console.log("📊 Auth check response:", response);

      // Handle nested response structure
      if (response.success && response.data) {
        const data = response.data as any;
        if (data.success !== undefined) {
          return data.success !== false;
        }
      }

      return response.success;
    } catch (error) {
      console.log("❌ Auth check failed:", error);
      return false;
    }
  }
}

export default AuthAPI;
